---
title: "Trading Commodities CFDs: Gold, Oil, Silver"
slug: trading-commodities
shortTitle: "Trading Commodities"
description: "How gold, oil, silver and other commodity CFDs behave, what tends to move them, how contract size turns price moves into USDT, and the hours and costs to check."
level: intermediate
category: markets
course: intermediate
order: 12
topics: [commodities, cfds, macro-news]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/trading-commodities.svg
faqs:
  - q: "Do I own any gold or oil when I trade a commodity CFD?"
    a: "No. You hold a contract that pays out the change in price, settled in USDT. There is no physical delivery, storage or futures expiry for you to manage."
  - q: "What is the difference between OIL and BRENT?"
    a: "They are two crude oil benchmarks. OIL follows West Texas Intermediate, the US benchmark, while BRENT follows North Sea crude, the reference for much of the oil traded internationally. They usually move together, but not by identical amounts."
  - q: "Can I trade commodities at the weekend?"
    a: "Generally not. Commodity markets follow the hours of the underlying market, which is usually closed at the weekend. Check the trading hours on the market's page, and remember that prices can reopen some distance from where they closed."
  - q: "Why did gold fall on a day with bad economic news?"
    a: "Gold's usual drivers can pull in different directions. A stronger dollar, rising interest rate expectations or investors selling gold to cover losses elsewhere can outweigh safe-haven buying. Links between news and price are tendencies, not rules."
  - q: "Why does silver tend to move more than gold?"
    a: "Silver is a smaller market with heavy industrial demand as well as investment demand, so it reacts to both. That usually means larger percentage swings than gold, which is worth reflecting in your position size."
---

Commodities are raw materials: metals such as gold, silver and copper, energy such as crude oil and natural gas, and crops such as corn. On {{BRAND}} you trade them as CFDs settled in USDT, shown as GOLD/USDT, SILVER/USDT and OIL/USDT, with BRENT, NATGAS, COPPER and CORN alongside.

Commodity prices react to forces crypto traders may not be used to: supply disruptions, inventory reports, interest rates, the US dollar and even the weather. This lesson covers what tends to move the main commodities, how contract size turns a price move into USDT, and the trading hours, gaps and costs to check before you place a trade.

<Warning title="Commodity CFDs are leveraged products">

Commodity CFDs are traded on margin, so losses are magnified just like gains, and they are calculated on the full position value. Energy markets can move several percent in a day, and prices can gap over weekends. Many traders lose money trading CFDs; only risk money you can afford to lose.

</Warning>

<CTA to="/trade/GOLD" />

<KeyTakeaways>

- **Each commodity has its own drivers.** Gold reacts to interest rates, the dollar and stress; oil to supply decisions, inventories and growth; silver to both investment and industrial demand.
- **Contract size turns points into USDT.** One lot of gold is 100 ounces, so a 0.05 lot position gains or loses 5 USDT for every $1 move. Check each market's lot size.
- **Hours, gaps and swaps differ from crypto.** Commodities close at weekends, can gap at the open and are charged swap on weekdays, three times on the triple-swap day.
- **Volatility varies widely.** Natural gas and silver often move more than gold in percentage terms, so size each position from its stop, not from habit.

</KeyTakeaways>

## What you are trading

A commodity CFD follows a reference price for the commodity, quoted in US dollars per unit: per troy ounce for gold and silver, per barrel for crude oil, per pound for copper and per bushel for corn. You never take delivery and there is no expiry date to manage. You hold the position for as long as you choose, paying or receiving a daily swap.

Each market's page under Markets has a **Key facts** card with the benchmark the price follows and the reports that tend to move it, and a card showing how the CFD trades here: lot size, leverage range, fee, swap and trading hours. Read both before your first trade in a new commodity.

{/* TODO: verify with platform — card names on the asset page and the list of commodity markets currently offered. */}

## What moves the main commodities

### Gold

Gold pays no interest, so it tends to look less attractive when **real interest rates** (interest rates minus inflation) rise and more attractive when they fall. It is priced in dollars, so a stronger dollar often weighs on it. It also draws **safe-haven** buying in times of financial or political stress, and central-bank purchases add steady demand. US inflation data, Federal Reserve decisions and US jobs figures are the scheduled events traders usually watch.

### Oil

Crude oil is about physical supply and demand. Production decisions by the OPEC+ group of producers, weekly US inventory reports, economic growth and conflicts or shipping disruption in producing regions can all move it sharply. OIL follows West Texas Intermediate (WTI), the US benchmark; BRENT follows North Sea crude, the international reference. They usually move together, but the gap between them changes.

### Silver and the rest

Silver sits between two worlds. It is a precious metal that often follows gold, and an industrial metal used in electronics and solar panels. That double demand usually means bigger percentage swings than gold. Copper tends to follow global construction and manufacturing. Natural gas is seasonal and weather-driven, with sharp moves around weekly storage data. Corn follows the weather in growing regions and government crop reports.

<CompareTable caption="Main commodity CFDs and what tends to move them">

| Market | Main drivers | Scheduled events often watched |
| --- | --- | --- |
| GOLD | Real interest rates, US dollar, safe-haven demand | US inflation (CPI), Fed decisions, US jobs data |
| SILVER | Gold's drivers plus industrial demand | Inflation data, manufacturing surveys |
| OIL / BRENT | Producer supply decisions, inventories, growth, conflict | Weekly US inventory report, OPEC+ meetings |
| NATGAS | Weather, storage levels, exports | Weekly US storage report, weather forecasts |
| COPPER | Global industry and construction | Chinese manufacturing data |
| CORN | Weather, harvests, crop reports | US government crop reports |

</CompareTable>

These are tendencies, not rules. Gold can fall on a "bad news" day if the dollar jumps, and oil can shrug off a supply headline if demand is weakening. For more on how data releases move prices, see [Fundamental Drivers](/academy/article/fundamental-drivers).

## Contract size: turning points into USDT

Commodity prices are quoted per unit, and a **lot** is a fixed number of units. On {{BRAND}}, one lot of gold is 100 troy ounces. Other markets have their own lot sizes. Switching the size toggle to **Lots** shows the contract size, the minimum and step, the units and the notional (the full position value) before you trade.

<Example title="A gold position in USDT (illustrative prices)">

You buy 0.05 lot of GOLD/USDT at 2,400. That is 0.05 × 100 = **5 ounces**.

- Position value: 5 × 2,400 = **12,000 USDT**.
- Value of a $1 move: 5 × 1 = **5 USDT**.
- Gold rises to 2,430: (2,430 − 2,400) × 5 = **+150 USDT** before costs.
- Gold falls to 2,385: (2,385 − 2,400) × 5 = **−75 USDT** before costs.
- At 20x leverage, margin required is 12,000 ÷ 20 = **600 USDT**. The profit and loss above do not change.

</Example>

Oil works the same way with barrels. A sell position of 100 barrels at 75 is worth 7,500 USDT, and every $1 move is worth 100 USDT. A rise to 76.20 costs (76.20 − 75) × 100 = 120 USDT. Because a $1 move in oil is a far larger percentage than a $1 move in gold, the **Value of $1 move** line in the order summary is the quickest sanity check on any commodity trade.

<ProTip>

Size commodity trades from the stop, not from a favorite lot size. If you will accept a 60 USDT loss and your gold stop is 12 dollars away, your size is 60 ÷ 12 = 5 ounces, or 0.05 lot. A wider stop means a smaller position. [Position Sizing](/academy/article/position-sizing) walks through the method.

</ProTip>

## Hours, gaps and swaps

Unlike crypto, commodity markets do not trade around the clock. They generally follow the hours of the underlying market: open through the week, often with a short daily break, and closed at the weekend. The trade screen shows when a market is closed, and the market's page lists its **Trading hours**.

{/* TODO: verify with platform — trading hours for each commodity market and how closures are shown on the trade screen. */}

When a market reopens after a weekend or a break, the first price can be well away from the last one. This is a **gap**. If the price jumps past your stop loss, the stop is not filled at its level; it closes at the next available price.

<Warning>

A stop loss fills at market once triggered, so a gap, or a fast move after an inventory report or a producer meeting, can close you at a worse price than your stop. If you hold commodity positions over a weekend, keep your size small enough that a gap is survivable, and keep plenty of free margin.

</Warning>

Holding past {{SWAP_HOUR}} adds a **swap**, a daily charge or credit worked out from the position value and the market's long or short swap rate. Commodity markets are charged on weekdays only, with three days' worth on {{SWAP_TRIPLE_DAY}} to cover the weekend. The market page shows **Swap long / short**, and [Trading Costs](/academy/article/trading-costs-swaps-fees) has a worked example.

## Before you trade a commodity

<Steps>
<Step title="Check the calendar">

Open the **Economic calendar** on the Markets page and look for high-impact events tied to your market, such as inflation data for gold or an inventory report for oil.

</Step>
<Step title="Check the contract">

Note the lot size, minimum volume and step, and the trading hours on the market's page.

</Step>
<Step title="Size from your stop">

Decide your maximum loss in USDT, then work out the size from the stop distance, as in the tip above.

</Step>
<Step title="Set TP / SL and read the summary">

Turn on **TP / SL**, then check **Margin required**, **Fee** and **Value of $1 move** before you press Buy or Sell.

</Step>
</Steps>

## Conclusion

Commodity CFDs let you trade gold, oil, silver and other raw materials from the same USDT account as your crypto, but they behave differently. Each has its own drivers, from interest rates and the dollar to inventories and the weather. Contract size decides what each price move is worth, and trading hours, weekend gaps and weekday swaps all shape the trade. Learn one or two markets well, check the calendar, size from your stop, and remember that leverage magnifies losses as much as gains.
